/**
 * Home "Top marketing assets": the brand's tracking links (`useBrandLinks`) joined with ONE grouped stats read over
 * the Home interval (`daily_affiliate_activity` aggregated on `campaign`), ranked per asset by `top-marketing-assets.ts`.
 *
 * The window is the one every other Home section reads (`homeWindow`, end-exclusive), and ranges that include today
 * re-read on the same 5-minute cadence (`useRefreshTick`). The read goes through the stats slice, keyed by brand,
 * window and grouping, so switching ranges back and forth reuses earlier answers and a background refresh keeps the
 * rows on screen (the skeleton shows only while nothing is cached).
 */
import React from 'react';
import { useTranslation } from 'react-i18next';

import { useAppDispatch, useAppSelector } from '@/store';
import {
  fetchStatsDataSet,
  selectStatsDataSet,
  selectStatsDataSetError,
  selectStatsDataSetLoading,
  type StatsDataSetRef,
} from '@/store/slices/ownerHubStats';

import type { HomePeriod } from '../../mock-data/types';
import { useBrandLinks } from '../creatives/useBrandLinks';
import { useBrandSettled } from '../website/useBrandSettled';
import { homeWindow, intervalIncludesToday } from './stats-mapping';
import { TOP_ASSETS_STATS, topMarketingAssets, type TopAssetRow } from './top-marketing-assets';
import { LIVE_REFRESH_MS, useRefreshTick } from './useRefreshTick';

/**
 * - `loading`: the brand, its links or (when it has links) the stats are still on their way;
 * - `no-links`: the brand has no tracking links yet;
 * - `no-players`: it has links, but none brought a registration or FTD in the range;
 * - `error`: the links or the stats could not be read;
 * - `ok`: `rows` holds the ranked assets.
 */
export type TopAssetsStatus = 'loading' | 'no-links' | 'no-players' | 'error' | 'ok';

export interface TopAssetsResult {
  status: TopAssetsStatus;
  rows: TopAssetRow[];
  /** Read the brand's links again (an asset composer opened in place may have renamed or moved one). */
  reloadLinks: () => void;
}

const { dataSet, aggregateOn } = TOP_ASSETS_STATS;

/**
 * @param casinoInstanceId Active brand id (`displayBrand?.id`).
 * @param interval The Home range.
 */
export const useTopMarketingAssets = (casinoInstanceId: string | undefined, interval: HomePeriod): TopAssetsResult => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const brandSettled = useBrandSettled();
  const links = useBrandLinks();

  const tick = useRefreshTick(intervalIncludesToday(interval), LIVE_REFRESH_MS);
  // `tick` is intentional (as in useHomeStats): it re-reads the clock so the window rolls over at midnight.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const window = React.useMemo(() => homeWindow(interval, Date.now()), [interval, tick]);

  const ref: StatsDataSetRef | undefined =
    casinoInstanceId
      ? { casinoInstanceId, dataSet, startDate: window.startDate, endDate: window.endDate, aggregateOn: [...aggregateOn] }
      : undefined;
  const entry = useAppSelector((s) => selectStatsDataSet(s, ref));
  const statsLoadingFlag = useAppSelector((s) => selectStatsDataSetLoading(s, ref));
  const statsError = useAppSelector((s) => selectStatsDataSetError(s, ref));

  // Syncs with the network: the grouped stats read for the brand and window (re-read on the live tick).
  React.useEffect(() => {
    if (!casinoInstanceId) return;
    void dispatch(
      fetchStatsDataSet({
        casinoInstanceId,
        dataSet,
        startDate: window.startDate,
        endDate: window.endDate,
        aggregateOn: [...aggregateOn],
      }),
    );
  }, [dispatch, casinoInstanceId, window.startDate, window.endDate, tick]);

  const casinoLabel = t('Casino');
  const rows = React.useMemo(
    () => (entry ? topMarketingAssets(entry.data, links.byCampaign, casinoLabel) : []),
    [entry, links.byCampaign, casinoLabel],
  );

  // While no tenant is known the links hook answers "no links" at once; only a settled brand without one means none.
  const linksPending = links.tenantId ? links.loading : !brandSettled;
  const statsPending = !casinoInstanceId || (entry === undefined && (statsLoadingFlag || statsError === undefined));

  let status: TopAssetsStatus;
  if (linksPending) status = 'loading';
  else if (links.error && links.links.length === 0) status = 'error';
  else if (links.links.length === 0) status = 'no-links';
  else if (statsPending) status = 'loading';
  else if (entry === undefined) status = 'error';
  else status = rows.length > 0 ? 'ok' : 'no-players';

  return { status, rows: status === 'ok' ? rows : [], reloadLinks: links.reload };
};

export default useTopMarketingAssets;
